'use client'

import { ReactNode, useEffect, useMemo, useState } from 'react'
import { useEnvContext } from 'next-runtime-env'
import { useAuth } from '@/dc-shared-web/src/providers/AuthProvider'
import { useSearchParams } from 'next/navigation'
import { SELECTED_TEAM_ID_KEY } from '@dc-shared-web/lib/constants'
import { useGetTeamSolutionTypes } from '@/dc-shared-web/src/hooks/api/team/useGetTeamSolutions'
import { parseFullName } from '@/app/core/utils/nameUtils'

export function ChameleonProvider({ children }: { children: ReactNode }) {
  const { NEXT_PUBLIC_CHAMELEON_ENABLED } = useEnvContext()
  const [isChameleonEnabled, setIsChameleonEnabled] = useState(
    NEXT_PUBLIC_CHAMELEON_ENABLED === 'true'
  )

  const selectedTeamIdParam = useSearchParams().get(SELECTED_TEAM_ID_KEY)
  const { user } = useAuth()

  const selectedTeam = useMemo(() => {
    if (selectedTeamIdParam) {
      const team = user.teams.find((t) => t.id === selectedTeamIdParam)
      setIsChameleonEnabled(!!team)
      return team
    }
    // ignoring super admin since we need current team id for chameleon
    const team = user.teams.find((t) => t.id === user._currOfficeUUID)
    setIsChameleonEnabled(!!team)
    return team
  }, [selectedTeamIdParam, user])

  const { data: teamSolutions, isLoading: isTeamSolutionsLoading } =
    useGetTeamSolutionTypes({
      teamId: selectedTeam?.teamId,
      enabled: !!selectedTeam && isChameleonEnabled,
    })

  // Call identify whenever user or selected team changes
  useEffect(() => {
    if (!isChameleonEnabled || isTeamSolutionsLoading) return
    if (!user || !user.uuid) {
      console.error('Missing required user data for Chameleon identify')
      return
    }

    identifyUser()
    function identifyUser() {
      if (!window.chmln?.identify) {
        return
      }
      const { firstName, lastName } = parseFullName(user.name)
      const identifyData = {
        uid_hash: user.chameleon_hash,
        email: user.email,
        name: user.name,
        first_name: firstName,
        last_name: lastName,
        created: user.created_at,
        is_admin_on_selected_team: !!selectedTeam?.is_admin,
        company: {
          uid: selectedTeam?.customer_id || null,
          name: selectedTeam?.name || null,
          customer_id: selectedTeam?.customer_id || null,
          internal: selectedTeam?.is_internal || false,
          partner: selectedTeam?.is_partner || false,
          trial: selectedTeam?.is_trial || false,
          created: selectedTeam?.created_at || null,
          entitlement_crunch_assist:
            selectedTeam?.entitlements?.crunch_assist || false,
          entitlement_word: selectedTeam?.entitlements?.word_access || false,
          entitlement_sso: selectedTeam?.entitlements?.use_sso || false,
          entitlement_procore:
            selectedTeam?.entitlements?.procore_access || false,
          entitlement_cheat_sheet:
            selectedTeam?.entitlements?.cheat_sheet_access || false,
          entitlement_high_security:
            selectedTeam?.entitlements?.high_security || false,
          solution_types:
            teamSolutions?.map((teamSolution) => teamSolution.name) || [],
        },
      }
      window.chmln?.identify(user.uuid, identifyData)
    }
  }, [
    isChameleonEnabled,
    user,
    selectedTeam,
    teamSolutions,
    isTeamSolutionsLoading,
  ])

  return children
}
